/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as Msdyn365 from '@msdyn365-commerce/core';
import * as React from 'react';
import {
    ILxs2ColumnContentBlockAdditionalContentItemViewProps,
    ILxs2ColumnContentBlockAdditionalContentProps,
    ILxs2ColumnContentBlockAdditionalContentViewProps
} from '../lxs-2-column-content-block.data';
import { IAdditionalContentData, ILinksData } from '../lxs-2-column-content-block.props.autogenerated';

const renderHeading = (
    additionalContentHeading: string,
    props: ILxs2ColumnContentBlockAdditionalContentProps,
    itemIndex: number,
    CLASS_NAME: string
) => {
    const requestContext = props.requestContext;
    return (
        <Msdyn365.Text
            text={additionalContentHeading}
            tag='p'
            className={`${CLASS_NAME}__additional-content-text`}
            editProps={{
                onEdit: event => {
                    props.handleAdditionalTextChange(itemIndex, event);
                },
                requestContext
            }}
        />
    );
};

const renderParagraph = (
    additionalContentParagraphText: string,
    props: ILxs2ColumnContentBlockAdditionalContentProps,
    itemIndex: number,
    CLASS_NAME: string
) => {
    const requestContext = props.requestContext;
    return (
        <Msdyn365.Text
            text={additionalContentParagraphText}
            tag='p'
            className={`${CLASS_NAME}__additional-content-paragraph`}
            editProps={{
                onEdit: event => {
                    props.handleAdditionalParagraphChange(itemIndex, event);
                },
                requestContext
            }}
        />
    );
};

const renderLinks = (
    ctaLink: ILinksData,
    props: ILxs2ColumnContentBlockAdditionalContentProps,
    itemIndex: number,
    linkIndex: number,
    CLASS_NAME: string
) => {
    const editableLink: Msdyn365.ILinksData = {
        ariaLabel: ctaLink.ariaLabel,
        className: `${CLASS_NAME}__additional-content-cta-link`,
        linkText: ctaLink.linkText,
        linkUrl: ctaLink.linkUrl.destinationUrl,
        openInNewTab: ctaLink.openInNewTab,
        role: 'link'
    };
    const requestContext = props.requestContext;
    return (
        <Msdyn365.Link
            key={linkIndex}
            link={editableLink}
            editProps={{
                onTextChange: event => {
                    props.handleAdditionalLinkTextChange(linkIndex, itemIndex, event);
                },
                requestContext
            }}
        />
    );
};

const assembleNode = (
    item: IAdditionalContentData,
    props: ILxs2ColumnContentBlockAdditionalContentProps,
    itemIndex: number,
    CLASS_NAME: string
): ILxs2ColumnContentBlockAdditionalContentItemViewProps => {
    const headingNode = item.heading && renderHeading(item.heading, props, itemIndex, CLASS_NAME);
    const paragraphNode = item.subtext && renderParagraph(item.subtext, props, itemIndex, CLASS_NAME);
    const linksNode = item.links?.map((ctaLink: ILinksData, index: number) => {
        return renderLinks(ctaLink, props, itemIndex, index, CLASS_NAME);
    });

    return {
        heading: headingNode,
        text: paragraphNode,
        links: linksNode,
        additionalContentItemContainer: { className: `${CLASS_NAME}__additional-content-container` },
        additionalContentItemLinks: { className: `${CLASS_NAME}__additional-content-cta` }
    };
};

export const contentBlockAdditionalContent = (
    props: ILxs2ColumnContentBlockAdditionalContentProps
): ILxs2ColumnContentBlockAdditionalContentViewProps | undefined => {
    const { additionalContent, CLASS_NAME } = props;
    if (!additionalContent) {
        return undefined;
    }

    const reactNodes: ILxs2ColumnContentBlockAdditionalContentItemViewProps[] = [];
    additionalContent.map((item: IAdditionalContentData, index: number) => {
        return reactNodes.push(assembleNode(item, props, index, CLASS_NAME));
    });

    return {
        additionalContentNode: { className: `${CLASS_NAME}__additional-content` },
        additionalContentItems: reactNodes
    };
};
